"use client";
import React from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import { Caveat } from "next/font/google";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

// Full sunburst SVG that surrounds the "Unleash" heading — matching golden reference
const SunburstLeft = () => (
  <svg
    width="140"
    height="140"
    viewBox="0 0 140 140"
    className="absolute -top-16 -left-24 pointer-events-none select-none"
    aria-hidden="true"
  >
    {/* Heart-tip top-left */}
    <path d="M 30 30 C 45 50, 60 65, 68 72" stroke="#fbb945" strokeWidth="3.5" strokeLinecap="round" fill="none" />
    <path d="M 30 30 C 28 22, 20 20, 17 25 C 14 30, 25 34, 30 30 Z" fill="#fbb945" />

    {/* Middle-top ray */}
    <path d="M 55 12 C 60 35, 63 55, 66 70" stroke="#fbb945" strokeWidth="3" strokeLinecap="round" fill="none" />
    <path d="M 55 12 C 52 4, 45 3, 43 8 C 41 13, 52 16, 55 12 Z" fill="#fbb945" />

    {/* Right-top ray */}
    <path d="M 90 18 C 82 38, 76 55, 72 70" stroke="#fbb945" strokeWidth="3" strokeLinecap="round" fill="none" />

    {/* Far-right horizontal ray */}
    <path d="M 116 52 C 98 58, 84 62, 74 68" stroke="#fbb945" strokeWidth="3" strokeLinecap="round" fill="none" />

    {/* Bottom-left diagonal ray */}
    <path d="M 28 88 C 42 82, 56 76, 66 72" stroke="#fbb945" strokeWidth="3" strokeLinecap="round" fill="none" />

    {/* Bottom short ray */}
    <path d="M 48 110 C 54 96, 60 84, 66 74" stroke="#fbb945" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

const SunburstRight = () => (
  <svg
    width="120"
    height="120"
    viewBox="0 0 120 120"
    className="absolute -top-12 -right-20 pointer-events-none select-none"
    aria-hidden="true"
  >
    {/* Heart-tip top-right */}
    <path d="M 95 28 C 80 48, 68 62, 60 68" stroke="#fbb945" strokeWidth="3.5" strokeLinecap="round" fill="none" />
    <path d="M 95 28 C 97 20, 105 18, 108 23 C 111 28, 100 32, 95 28 Z" fill="#fbb945" />

    {/* Far-left ray */}
    <path d="M 14 50 C 30 56, 44 62, 56 68" stroke="#fbb945" strokeWidth="3" strokeLinecap="round" fill="none" />

    {/* Top-center ray */}
    <path d="M 62 10 C 62 30, 61 50, 60 66" stroke="#fbb945" strokeWidth="3" strokeLinecap="round" fill="none" />
    <path d="M 62 10 C 60 2, 54 1, 52 6 C 50 11, 59 13, 62 10 Z" fill="#fbb945" />

    {/* Bottom-right ray */}
    <path d="M 100 88 C 86 80, 74 74, 64 70" stroke="#fbb945" strokeWidth="2.5" strokeLinecap="round" fill="none" />

    {/* Bottom short */}
    <path d="M 72 110 C 66 96, 62 83, 60 70" stroke="#fbb945" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

export default function IndustryCTA({ data, industryId }) {
  const slug = industryId || (data?.title ? data.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") : "");
  const targetHref = slug ? `/odoo/start/${slug}` : "/odoo/start";
  return (
    <section className="overflow-hidden bg-white/5 px-6 py-20 text-center sm:px-12 lg:px-24">
      <motion.div
        className="container relative mx-auto"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h3
          className={`relative mb-8 inline-block text-5xl sm:text-6xl md:text-7xl font-bold text-white ${caveat.className}`}
        >
          <SunburstLeft />
          <SunburstRight />
          <span className="text-[#f1833b]">Unleash</span>
          <br />
          <span className="text-white">your growth potential</span>
        </h3>

        <div className="mt-12 flex flex-col items-center gap-4">
          <Link href={targetHref}>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(241,131,59,0.4)" }}
              whileTap={{ scale: 0.97 }}
              className="rounded-xl bg-[#f1833b] px-14 py-3.5 text-xl font-semibold text-white transition-all hover:bg-[#e06b24] shadow-lg shadow-orange-500/30"
            >
              Start now
            </motion.button>
          </Link>

          <motion.img
            src="/odoo/arrows/secondary_arrow_sm_01.svg"
            alt=""
            className="h-8 w-8 mx-auto mt-1"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            loading="lazy"
          />

          <small className="text-gray-400 font-medium text-sm tracking-wide">15 days trial</small>
        </div>
      </motion.div>
    </section>
  );
}
